import type { RailState, RailStep } from "./stage.js";

/**
 * Where the build is, as six numbered steps across the top.
 *
 * Numbered because that is what makes it read as a spec rather than as a
 * loading bar — the design's own contents rail is `01 Build / 02 Test /
 * 03 Deploy`, and the numbers are what say "this is a defined sequence with a
 * known end" to someone who has been watching for three minutes.
 *
 * The labels are `PHASE_LABELS`, the same six words every other skin uses. See
 * the note in stage.ts: three skins over one pipeline should not name the same
 * five minutes three different ways.
 *
 * It wraps rather than scrolls. Six labelled steps do not fit across a 390px
 * phone and a horizontally scrolling progress indicator is a progress indicator
 * you cannot see the end of, which defeats the only thing it is for. Two rows of
 * three costs one row of height and keeps the whole sequence visible at once.
 *
 * The steps only ever advance, because the value behind them only ever advances:
 * the stream reducer refuses a backwards phase, so a rail that jumps back and
 * reads as failure is impossible here by construction.
 */
export function ProgressRail({ steps }: { steps: RailStep[] }) {
  return (
    <ol
      aria-label="Build progress"
      className="flex shrink-0 flex-wrap items-center justify-center gap-y-1 border-b border-deck-line px-3 py-2"
    >
      {steps.map((step, i) => (
        <li
          key={step.label}
          aria-current={step.state === "active" || step.state === "halted" ? "step" : undefined}
          className="flex items-center"
        >
          <span
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 ${
              step.state === "active" || step.state === "halted" ? "bg-deck-blue-soft" : ""
            }`}
          >
            <span
              aria-hidden
              className={`size-1.5 shrink-0 rounded-full ${DOT[step.state]} ${
                step.pulse ? "animate-[deckPulse_1.4s_ease-in-out_infinite]" : ""
              }`}
            />
            {/* The ordinal is always the mono face and always faint. It is
                scaffolding for reading the sequence, not a thing to read. */}
            <span className="font-deck-mono text-[10px] tabular-nums text-deck-text-faint">
              {step.ordinal}
            </span>
            <span className={`text-[12.5px] ${TEXT[step.state]}`}>{step.label}</span>
          </span>
          {/* A step on its own says "backend". A step with a line running out of
              it says "backend, then" — which is what turns six chips into a
              sequence with a direction. */}
          {i < steps.length - 1 && <span aria-hidden className="h-px w-2 bg-deck-line sm:w-3" />}
        </li>
      ))}
    </ol>
  );
}

/**
 * `halted` is the step a stopped build died in.
 *
 * It reads as ink rather than as the accent, because the accent means "here, and
 * now" and a stopped build is neither. Sharing the accent made a halted step
 * differ from an active one only by the missing pulse — a distinction nobody
 * catches on a still screen, and one that vanishes entirely under reduced
 * motion.
 */
const DOT: Record<RailState, string> = {
  done: "bg-deck-live",
  active: "bg-deck-blue",
  halted: "bg-deck-text-faint",
  pending: "bg-deck-line",
};

const TEXT: Record<RailState, string> = {
  done: "text-deck-text-soft",
  active: "font-semibold text-deck-text",
  halted: "font-semibold text-deck-text",
  pending: "text-deck-text-faint",
};
